import { type RefObject } from 'react';
import type { GraphCounts } from './memory-graph.ts';
import PanelEmpty from './PanelEmpty.tsx';
import './MemoryGraph.css';

interface MemoryGraphProps {
  canvasRef: RefObject<HTMLCanvasElement>;
  popRef: RefObject<HTMLDivElement>;
  counts: GraphCounts;
}

/** Inspector "Memory" section: the live memory-graph canvas with a hover/select
 *  detail popover, a count HUD, and a type legend. The simulation is driven by
 *  `useMemoryGraph`, which owns the canvas referenced here. The section header
 *  lives in the inspector accordion, so this renders body content only. */
export default function MemoryGraph({ canvasRef, popRef, counts }: MemoryGraphProps) {
  const empty = counts.nodes === 0;
  return (
    <section className="om-memory">
      <div className="om-memory__canvas-wrap">
        <canvas className="om-memgraph" ref={canvasRef} />
        <div className="om-mempop" ref={popRef} />
        {/* Keep the canvas mounted while empty so its ResizeObserver still
            measures; overlay the explanatory placeholder on top. */}
        {empty ? (
          <PanelEmpty>
            Your memory graph builds here as you chat — the facts and context
            this agent remembers.
          </PanelEmpty>
        ) : null}
        {/* The count HUD + legend only make sense once the graph has nodes;
            while empty the placeholder above stands in for them. */}
        {!empty ? (
          <>
            <div className="om-memory__count">
              <div>
                <b>{counts.nodes}</b> memories
              </div>
              <div>
                <b>{counts.edges}</b> links
              </div>
              <div>
                <b className="hot">{counts.active}</b> in context
              </div>
            </div>
            {/* Only the node types the runtime actually produces are listed. The
                runtime emits two memory record kinds — `turn` (→ agent reply) and
                `fact` (→ semantic fact) — plus the per-turn prompt and a per-turn
                aggregate `tool` node (one node standing in for all of the turn's
                tool calls); entity/reflection have no producer yet. Prompts of one
                session are threaded by a dotted line. See memoryGraph.ts MemType. */}
            <div className="om-memory__legend">
              <span>
                <i className="om-swatch om-swatch--prompt" /> user prompt
              </span>
              <span>
                <i className="om-swatch om-swatch--response" /> agent reply
              </span>
              <span>
                <i className="om-swatch om-swatch--fact" /> semantic fact
              </span>
              <span>
                <i className="om-swatch om-swatch--tool" /> tool calls
              </span>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
